import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { api, ApiError } from "./api/client";
import { purgeApiCache } from "./sw-register";
import { clearScreenCache } from "./use-screen";

type Me = { id: string; name: string; email: string | null; avatarUrl: string | null; role: string };

/**
 * 확인이 끝난 내 계정. **이미 받아온 값을 나눠 쓰는 것뿐이다** — 관리자 메뉴를
 * 보이려고 `/api/me`를 한 번 더 부르면 화면마다 왕복(≈190ms)이 하나 는다.
 */
const MeContext = createContext<Me | null>(null);

/**
 * 마지막으로 이 기기에서 확인된 사용자. **서비스 워커의 API 캐시를 언제 비울지**
 * 판단하는 데만 쓴다 — 공용 기기에서 앞사람 여행이 다음 사람에게 보이면 안 된다.
 */
const LAST_USER_KEY = "gotravel:lastUserId";

function rememberUser(id: string): void {
  try {
    if (localStorage.getItem(LAST_USER_KEY) !== id) {
      clearScreenCache();
      void purgeApiCache();
      // 이전 사용자의 계정 캐시도 같이 버린다 — 오프라인 진입으로 되살아나면 안 된다.
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith("gotravel:me:")) localStorage.removeItem(key);
      }
      localStorage.setItem(LAST_USER_KEY, id);
    }
  } catch {
    // 저장소가 막혀 있으면(사생활 보호 모드 등) 판단할 수 없다 — 안전한 쪽으로 비운다.
    clearScreenCache();
    void purgeApiCache();
  }
}
/**
 * 마지막으로 확인된 내 계정. **Clerk 사용자 ID별로** 저장한다 — 공용 기기에서
 * 다른 사람이 로그인했을 때 앞사람 이름이 잠깐이라도 보이면 안 된다.
 *
 * 이게 있으면 `/api/me`를 기다리지 않고 화면을 바로 그린다. 실측(재방문):
 * Clerk 확인 250ms → `/api/me` 160ms → 화면 데이터 300ms로 **세 번 줄서던 것이**
 * 두 번이 된다. 왕복이 비싼 모바일에서 차이가 더 크다.
 */
const ME_KEY = (userId: string) => `gotravel:me:${userId}`;

function readCachedMe(userId: string | null | undefined): Me | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(ME_KEY(userId));
    return raw ? (JSON.parse(raw) as Me) : null;
  } catch {
    return null;
  }
}

/** 마지막 Clerk 사용자 id. 오프라인 진입 때 어떤 캐시를 읽을지 정하는 열쇠다. */
const LAST_CLERK_KEY = "gotravel:lastClerkUserId";

function writeCachedMe(userId: string | null | undefined, me: Me): void {
  if (!userId) return;
  try {
    localStorage.setItem(LAST_CLERK_KEY, userId);
    localStorage.setItem(ME_KEY(userId), JSON.stringify(me));
  } catch {
    // 저장소가 막혀 있으면 캐시 없이 가면 된다 — 느릴 뿐 동작은 같다.
  }
}

export const useMe = () => useContext(MeContext);

/**
 * 로그인은 됐는데 **우리 users 행이 아직 없는** 상태를 메운다.
 *
 * 왜 필요한가: Worker의 인증 미들웨어는 계정을 만들지 않는다(모든 요청이 쓰기
 * 경로가 되고, 믿을 만한 이름·이메일도 토큰에 없다 — api/src/auth.ts 주석).
 * 행을 만드는 건 `POST /api/session/bootstrap` 한 곳이고, 그걸 부르는 게 여기다.
 *
 * 흐름은 서버 쪽 설계 그대로다:
 *   GET /api/me → 200이면 끝
 *              → 401/404면 bootstrap 한 번 → 다시 GET /api/me
 *
 * 이관된 기존 사용자도 이 경로로 들어온다. 그들의 `auth_key`는 아직
 * `google:...`이라 첫 요청이 401인데, bootstrap이 **검증된 이메일로 기존 행을
 * 찾아 연결**한다(새 행을 만들지 않는다 — linkClerkIdentity).
 */
/**
 * 마지막으로 이 기기에서 로그인했던 Clerk 사용자. 오프라인에서 Clerk을
 * 기다리지 않고 들어가려면 누구였는지 알아야 한다.
 */
export function lastSignedInUserId(): string | null {
  try {
    return localStorage.getItem(LAST_CLERK_KEY);
  } catch {
    return null;
  }
}

/** 오프라인으로 들어갈 수 있는가 — 그 사람의 계정 정보가 남아 있어야 한다. */
export function hasOfflineSession(): boolean {
  const id = lastSignedInUserId();
  return !!id && readCachedMe(id) !== null;
}

export function SessionGate({
  children,
  offlineUserId,
}: {
  children: React.ReactNode;
  /**
   * 오프라인 진입. Clerk이 세션을 확인하려면 네트워크가 필요해서, 끊긴 채로
   * 앱을 다시 열면 `SignedIn`·`SignedOut` 어느 쪽도 안 그려진다. 그때는 마지막
   * 사용자로 **캐시만 보여 준다** — 쓰기는 어차피 API가 막는다.
   */
  offlineUserId?: string;
}) {
  const { userId: clerkUserId } = useAuth();
  const userId = offlineUserId ?? clerkUserId;
  // **캐시가 있으면 기다리지 않는다.** 화면을 먼저 그리고 뒤에서 확인한다
  // (stale-while-revalidate). 한 번만 읽는다 — 렌더마다 읽으면 그 값이 매번
  // 새 객체라 아래 effect가 무한히 다시 돈다.
  const [cached] = useState(() => readCachedMe(userId));
  const [state, setState] = useState<"checking" | "ready" | "failed">(cached ? "ready" : "checking");
  const [me, setMe] = useState<Me | null>(cached);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    // 오프라인 진입이면 확인할 방법이 없다 — 캐시로 그린 채로 둔다.
    if (offlineUserId) return;

    (async () => {
      try {
        const found = await api.get<Me>("/api/me");
        rememberUser(found.id);
        writeCachedMe(userId, found);
        if (!cancelled) {
          setMe(found);
          setState("ready");
        }
        return;
      } catch (err) {
        // 이미 캐시로 화면을 그린 뒤라면 **조용히 넘어간다.** 확인에 실패했다고
        // 쓰던 화면을 뺏을 이유가 없다 — 진짜 권한 문제면 각 API가 401을 준다.
        if (cached) return;
        // 계정이 아직 안 붙은 경우만 부트스트랩으로 넘어간다. 그 외(네트워크 등)는
        // 재시도해도 같으므로 그대로 알린다.
        const status = err instanceof ApiError ? err.status : 0;
        if (status !== 401 && status !== 404) {
          if (!cancelled) {
            setMessage(err instanceof Error ? err.message : "연결에 실패했습니다");
            setState("failed");
          }
          return;
        }
      }

      try {
        await api.post("/api/session/bootstrap");
        const created = await api.get<Me>("/api/me");
        rememberUser(created.id);
        writeCachedMe(userId, created);
        if (!cancelled) {
          setMe(created);
          setState("ready");
        }
      } catch (err) {
        if (!cancelled) {
          setMessage(err instanceof Error ? err.message : "계정 설정에 실패했습니다");
          setState("failed");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // userId는 Clerk이 확정한 뒤 안 바뀐다. cached는 첫 렌더에 고정된 값이다.
  }, [userId, cached, offlineUserId]);

  if (state === "checking") {
    return (
      <div className="flex min-h-dvh items-center justify-center text-sm text-muted-foreground">
        계정을 확인하는 중…
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-muted-foreground">{message}</p>
        <button
          className="rounded-md border px-4 py-2 text-sm"
          onClick={() => window.location.reload()}
        >
          다시 시도
        </button>
      </div>
    );
  }

  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}
